import type { CreasePattern } from '@/origami/types';
import type { Vec2 } from '@/geometry/vec2';
import {
  blankSheet,
  boundaryLoop,
  patternFromSegments,
  raysFromPoint,
  rectangleBoundary,
  type RawSegment,
} from './builders';

export interface ExampleDefinition {
  id: string;
  name: string;
  description: string;
  /** What makes it mathematically interesting. */
  mathematics: string;
  recommendedSolver: 'rigid' | 'compliant';
  build: () => CreasePattern;
}

const p = (x: number, y: number): Vec2 => ({ x, y });

/**
 * Miura-ori: straight rows, zigzag columns.
 *
 * `sectorAngle` is the acute sector angle between a straight row crease and a
 * zigzag crease, in degrees. It is the parameter that matters: at 90 degrees the
 * tessellation degenerates into a plain rectangular grid, which is not
 * rigid-foldable as a Miura at all, and the closer you get to 90 the stiffer and
 * more numerically delicate the mechanism becomes. Real Miura-ori sits around
 * 55-70 degrees.
 */
export function miuraOri(
  columns = 6,
  rows = 4,
  cellWidth = 1 / 6,
  cellHeight = 1 / 4,
  sectorAngle = 60,
): CreasePattern {
  const segments: RawSegment[] = [];
  const shear = cellHeight / Math.tan((sectorAngle * Math.PI) / 180);
  const shift = (j: number): number => (j % 2 === 0 ? 0 : shear);
  const V = (i: number, j: number): Vec2 => p(i * cellWidth + shift(j), j * cellHeight);

  // Assignment. At a Miura vertex the two ZIGZAG creases share an assignment
  // and the two COLLINEAR row creases differ from each other, giving the 3:1
  // split Maekawa demands. Getting this backwards produces a pattern that still
  // passes Kawasaki and Maekawa but whose only rigid motion is the trivial one
  // of folding along the straight rows - the mechanism never deploys.
  //
  // The first-order check at the flat state makes the reason concrete: summing
  // rho_k * u_k over the creases at a vertex forces the two zigzag angles to be
  // equal, so they cannot carry opposite signs.
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i < columns; i++) {
      segments.push({
        a: V(i, j),
        b: V(i + 1, j),
        assignment: j === 0 || j === rows ? 'B' : (i + j) % 2 === 0 ? 'M' : 'V',
      });
    }
  }
  for (let i = 0; i <= columns; i++) {
    for (let j = 0; j < rows; j++) {
      const boundary = i === 0 || i === columns;
      segments.push({
        a: V(i, j),
        b: V(i, j + 1),
        assignment: boundary ? 'B' : i % 2 === 0 ? 'V' : 'M',
      });
    }
  }
  return patternFromSegments(segments, {
    title: 'Miura-ori',
    description: `${columns} x ${rows} Miura-ori tessellation.`,
    recommendedSolver: 'rigid',
  });
}

/** Yoshimura (diamond) pattern - folds to a cylinder, not flat. */
export function yoshimura(columns = 6, rows = 4, width = 1, height = 1): CreasePattern {
  const cw = width / columns;
  const ch = height / rows;
  const segments: RawSegment[] = [];
  const V = (i: number, j: number): Vec2 =>
    p(Math.min(width, Math.max(0, i * cw + (j % 2 === 1 ? cw / 2 : 0))), j * ch);

  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i < columns; i++) {
      segments.push({
        a: V(i, j),
        b: V(i + 1, j),
        assignment: j === 0 || j === rows ? 'B' : 'M',
      });
    }
  }
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i <= columns; i++) {
      const down = V(i, j);
      const upLeft = V(i - (j % 2 === 0 ? 1 : 0), j + 1);
      const upRight = V(i + (j % 2 === 0 ? 0 : 1), j + 1);
      if (i > 0) segments.push({ a: down, b: upLeft, assignment: 'V' });
      if (i < columns) segments.push({ a: down, b: upRight, assignment: 'V' });
    }
  }
  segments.push(
    { a: p(0, 0), b: p(0, height), assignment: 'B' },
    { a: p(width, 0), b: p(width, height), assignment: 'B' },
  );
  return patternFromSegments(segments, {
    title: 'Yoshimura pattern',
    description: 'Diamond/Yoshimura buckling pattern.',
    recommendedSolver: 'compliant',
  });
}

export const EXAMPLES: ExampleDefinition[] = [
  {
    id: 'blank-square',
    name: 'Blank square',
    description: 'An uncreased 1 x 1 sheet. The starting point for drawing your own pattern.',
    mathematics: 'One face, four boundary edges, zero degrees of freedom.',
    recommendedSolver: 'compliant',
    build: () => blankSheet(1, 1, 'Square sheet'),
  },
  {
    id: 'single-valley',
    name: 'Single valley fold',
    description: 'One valley crease across the middle of a square.',
    mathematics:
      'No interior vertices, so there are no loop-closure constraints: the single fold angle is a free variable and the rigid solver reaches the target exactly.',
    recommendedSolver: 'rigid',
    build: () =>
      patternFromSegments(
        [...rectangleBoundary(1, 1), { a: p(0, 0.5), b: p(1, 0.5), assignment: 'V' }],
        {
          title: 'Single valley fold',
          recommendedSolver: 'rigid',
        },
      ),
  },
  {
    id: 'single-mountain',
    name: 'Single mountain fold',
    description: 'The same crease with the opposite assignment, folding away from the viewer.',
    mathematics:
      'Identical geometry to the valley example with the fold angle negated - a direct check of the sign convention.',
    recommendedSolver: 'rigid',
    build: () =>
      patternFromSegments(
        [...rectangleBoundary(1, 1), { a: p(0, 0.5), b: p(1, 0.5), assignment: 'M' }],
        { title: 'Single mountain fold', recommendedSolver: 'rigid' },
      ),
  },
  {
    id: 'four-crease-vertex',
    name: 'Four-crease vertex',
    description:
      'A single degree-4 interior vertex with sector angles 70°, 110°, 110°, 70°.',
    mathematics:
      'The smallest interesting flat-foldable vertex. Kawasaki holds because opposite sectors sum to 180°, and Maekawa forces a 3:1 mountain/valley split. Rigid folding has exactly one degree of freedom.',
    recommendedSolver: 'rigid',
    build: () =>
      patternFromSegments(
        [
          ...rectangleBoundary(1, 1),
          ...raysFromPoint(p(0.5, 0.5), [0, 70, 180, 290], ['M', 'V', 'M', 'M']),
        ],
        { title: 'Four-crease vertex', recommendedSolver: 'rigid' },
      ),
  },
  {
    id: 'miura-ori',
    name: 'Miura-ori',
    description: 'The classic rigid-foldable tessellation used for deployable solar arrays.',
    mathematics:
      'Every interior vertex is degree 4 with sectors (θ, π−θ, π−θ, θ), so Kawasaki holds exactly. The whole tessellation is rigid-foldable with a single degree of freedom: one slider deploys the entire sheet.',
    recommendedSolver: 'rigid',
    build: () => miuraOri(),
  },
  {
    id: 'waterbomb-base',
    name: 'Waterbomb base',
    description: 'Both diagonals of a square plus one midline.',
    mathematics:
      'A degree-6 interior vertex with sectors 45°, 90°, 45°, 45°, 90°, 45°. Four mountains and two valleys give |M − V| = 2.',
    recommendedSolver: 'compliant',
    build: () =>
      patternFromSegments(
        [
          ...rectangleBoundary(1, 1),
          { a: p(0, 0), b: p(1, 1), assignment: 'M' },
          { a: p(1, 0), b: p(0, 1), assignment: 'M' },
          { a: p(0, 0.5), b: p(1, 0.5), assignment: 'V' },
        ],
        { title: 'Waterbomb base', recommendedSolver: 'compliant' },
      ),
  },
  {
    id: 'preliminary-base',
    name: 'Preliminary base',
    description: 'Both diagonals and both midlines of a square.',
    mathematics:
      'A degree-8 vertex with eight 45° sectors. Maekawa forbids a 4:4 split, so the mountain/valley assignment cannot share the pattern’s four-fold symmetry: one half-midline must differ from its opposite. That asymmetry is the whole lesson of this example.',
    recommendedSolver: 'compliant',
    build: () =>
      patternFromSegments(
        [
          ...rectangleBoundary(1, 1),
          { a: p(0, 0), b: p(1, 1), assignment: 'M' },
          { a: p(1, 0), b: p(0, 1), assignment: 'M' },
          { a: p(0.5, 0), b: p(0.5, 1), assignment: 'V' },
          { a: p(0, 0.5), b: p(0.5, 0.5), assignment: 'V' },
          { a: p(0.5, 0.5), b: p(1, 0.5), assignment: 'M' },
        ],
        { title: 'Preliminary base', recommendedSolver: 'compliant' },
      ),
  },
  {
    id: 'yoshimura',
    name: 'Yoshimura pattern',
    description: 'The diamond buckling pattern seen in crushed cylinders.',
    mathematics:
      'Interior vertices are degree 6 but the sectors do not satisfy Kawasaki, so the sheet cannot fold flat; it folds into a cylinder instead. A good demonstration that "not flat-foldable" is not the same as "not foldable".',
    recommendedSolver: 'compliant',
    build: () => yoshimura(),
  },
  {
    id: 'accordion',
    name: 'Accordion pleats',
    description: 'Five parallel creases alternating mountain and valley.',
    mathematics:
      'No interior vertices at all: five independent fold angles, so the rigid solver reports five degrees of freedom and reaches every target exactly.',
    recommendedSolver: 'rigid',
    build: () => {
      const segs: RawSegment[] = [...rectangleBoundary(1, 1)];
      for (let i = 1; i <= 5; i++) {
        segs.push({
          a: p(i / 6, 0),
          b: p(i / 6, 1),
          assignment: i % 2 === 1 ? 'M' : 'V',
        });
      }
      return patternFromSegments(segs, {
        title: 'Accordion pleats',
        recommendedSolver: 'rigid',
      });
    },
  },
  {
    id: 'box-net',
    name: 'Open box',
    description: 'A cross-shaped net with four valley creases that fold up into a box.',
    mathematics:
      'A non-rectangular paper boundary. The four creases meet no interior vertex, so the fold is unconstrained: it is a pure demonstration of face-tree propagation.',
    recommendedSolver: 'rigid',
    build: () => {
      const s = 1 / 3;
      const cross: Vec2[] = [
        p(s, 0), p(2 * s, 0), p(2 * s, s), p(1, s), p(1, 2 * s),
        p(2 * s, 2 * s), p(2 * s, 1), p(s, 1), p(s, 2 * s), p(0, 2 * s),
        p(0, s), p(s, s),
      ];
      return patternFromSegments(
        [
          ...boundaryLoop(cross),
          { a: p(s, s), b: p(2 * s, s), assignment: 'V' },
          { a: p(s, 2 * s), b: p(2 * s, 2 * s), assignment: 'V' },
          { a: p(s, s), b: p(s, 2 * s), assignment: 'V' },
          { a: p(2 * s, s), b: p(2 * s, 2 * s), assignment: 'V' },
        ],
        { title: 'Open box', recommendedSolver: 'rigid' },
      );
    },
  },
  {
    id: 'kirigami-cut',
    name: 'Kirigami cut',
    description: 'A square cut clean across the middle, with a crease in each half.',
    mathematics:
      'Cut edges sever connectivity instead of acting as hinges. The simulation mesh duplicates every vertex along the cut, so the two halves fold independently. (A slit that stops inside the paper is not supported yet - see the limitations note in docs/ARCHITECTURE.md.)',
    recommendedSolver: 'compliant',
    build: () =>
      patternFromSegments(
        [
          ...rectangleBoundary(1, 1),
          { a: p(0, 0.5), b: p(1, 0.5), assignment: 'C' },
          { a: p(0, 0.25), b: p(1, 0.25), assignment: 'V' },
          { a: p(0, 0.75), b: p(1, 0.75), assignment: 'M' },
        ],
        { title: 'Kirigami cut', recommendedSolver: 'compliant' },
      ),
  },
];

export const getExample = (id: string): ExampleDefinition | undefined =>
  EXAMPLES.find((e) => e.id === id);
